import { HashMap } from '@datorama/akita';
import { Period } from '../report/report.model';

export class ReportV4Resp<T> {
  columnOrder: string[];
  fileName: string;
  rows: T[];

  constructor(obj?: Partial<ReportV4Resp<T>>) {
    if (obj) {
      Object.assign(this, obj);
    }
  }

  withBuiltRows(rows: T[]) {
    this.rows = rows;
    return this;
  }
}

export class FormattedV4Report {
  code: string;
  type: Period;
  label: string;
  iam: boolean;
  custom: boolean;

  constructor(obj: Partial<FormattedV4Report>) {
    if (obj) {
      Object.assign(this, obj);
    }
  }
}

/**
 * Using filter or queryString
 * Filter with {key: value} style
 * QueryString with lucene style search query with escaped content
 * Exam:
 * - "queryString": "\"answered\" AND \"+6598300241\""
 * - "filter": { "a": "a1", "b": ["b1", "b2"] }
 */
export interface GetReportV4Payload {
  period: Period;

  orgUuid: string; // option
  endTime: string;
  startTime: string;
  scope?: string;
  teamUuid?: string; // scope = team
  filter?: any;
  queryString?: string;
  sort?: [{ time: 'desc' | 'asc' }];
  columns?: string[];
}

export class ReportV4Code {
  // v2
  static recentCallUnifiedHistory2 = 'unifiedHistory2.call.lite';
  static recentLegUnifiedHistory2 = 'unifiedHistory2.leg.lite';
  static unifiedHistory2 = 'unified.history2';
  static unifiedHistory2Leg = 'unified.history2.leg'; //for leg view; personal scope
  static unifiedHistory2CallWeb = 'unifiedHistory2.call.web.inapp';
  static unifiedHistory2CallWeb2 = 'unifiedHistory2.call.web.inapp2';
  static unifiedHistory2LegWeb = 'unifiedHistory2.leg.web.inapp';
  static unifiedHistory2LegWeb3 = 'unifiedHistory2.leg.web.inapp3';
  static unifiedHistory2LegHiddenWeb = 'unifiedHistory2.leg.hidden.web.inapp';
  static unifiedHistory2CallWebIvr = 'unifiedHistory2.call.ivr.web.inapp';
  static supportHubHistory2Sms = 'supporthub.sms.history.inapp';
  static supportHubHistory2CallWeb = 'supporthub.call.history.inapp';
  static autoAttendantCallJourney = 'unifiedHistory2.ivr';

  static loginHistory = 'login.history';
  static userManageLoginHistory = 'userManage.loginHistory';
  static txnJourney = {
    txn: 'inbox.call.txn.inapp',
    enterQueue: 'inbox.call.queue.inapp',
    leg: 'inbox.call.leg.inapp',
    callback: 'inbox.call.queue.callback.inapp'
  };

  // v1
  static unifiedHistorySms = 'unifiedHistory.sms.inapp';
  static unifiedHistoryFax = 'unifiedHistory.fax.web.inapp';

  static unifiedHistory = 'unified.history';
  static recentCallUnifiedHistory = 'unifiedHistory.call.lite';
  static chatUnifiedHistory = 'unifiedHistory.chat';
  static sellerGroupedBill = 'seller.groupedBill';
  static billingBuyerBalanceMovement = 'billing.buyer.balanceMovement';
  static callcenter = {
    agent: 'callcenter.agent',
    inbound: 'callcenter.inbound',
    inbound2: 'callcenter.inbound2',
    callback: 'callcenter.callback',
    overflow: 'callcenter.inbound.overflow',
    autodialer: 'callcenter.autodialer',
    activity: 'callcenter.agent.activity',
    performance: 'callcenter.agent.performance'
  };
  static communication = {
    userPerformance: 'commhub.user.performance.inapp4',
    userPerformanceByQueue: 'commhub.user.performance.byQueue.inapp4',
    userPerformanceByTimeQueue: 'commhub.user.performance.byTimeQueue.inapp4',
    userPerformanceByTime: 'commhub.user.performance.byTime.inapp4',
    callback: 'commhub.callback.inapp',
    callLive: 'commhub.queue.live.inapp2',
    state: 'user.state.curr',
    callbackEnded: 'commhub.callback.ended.inapp2',
    inboundEnded: 'commhub.queue.call.inbound.ended.inapp3',
    activity: 'commhub.user.activity',
    activityAgg: 'commhub.user.activity.agg',
    userAssignedCall: 'commhub.user.assigned.inapp2',
    queuePerformance: 'commhub.queue.performance.inapp3',
    queuePerformanceByTime: 'commhub.queue.performance.byTime.inapp3',
    inboundEnded4: 'commhub.queue.call.inbound.ended.inapp4',
    liveChatEnded: 'commhub.inbox.livechat.ended.inapp',
    whatsAppEnded: 'commhub.inbox.whatsapp.ended.inapp',
    supportTicketEnded: 'commhub.inbox.supportcenter.ended.inapp'
  };
  static dashboard = {
    inbound: 'callcenter.dashboard.inbound',
    callback: 'callcenter.dashboard.callback'
  };
  static unifiedHistoryDownload = {
    call: 'unifiedHistory.call',
    call2: 'unifiedHistory2.call',
    call2V2: 'unifiedHistory2.call2', // compare key with TypeExportCompare
    leg: 'unifiedHistory.leg',
    leg2: 'unifiedHistory2.leg',
    leg2v2: 'unifiedHistory2.leg2',
    callIvr: 'unifiedHistory2.call.ivr',
    legMe3: 'unifiedHistory2.leg.me3',
    legEveryone3: 'unifiedHistory2.leg.everyone3' // compare key with TypeExportCompare
  };
  static unifiedSmsHistoryDownload = {
    sms: 'unifiedHistory.sms'
  };
  static unifiedFaxHistoryDownload = {
    fax: 'unifiedHistory.fax'
  };
  static edge = {
    leg: 'edge.legCdr'
  };
  static sms = 'bmo.sms.troubleshooting';
}

export interface ArchiveTxnPayload {
  startTime: string;
  endTime: string;
  orgUuid: string;
}

export interface ArchiveTxnResp {
  customerUuid: string;
  channel: string;
  txnUuid: string;
  time: number;
}
export class StateReport {
  readonly stateMapping = {
    available: 'Available',
    busy: 'Busy',
    dnd: 'Away',
    offline: 'Offline',
    dialing: 'Dialing',
    talking: 'Talking',
    holding: 'Holding',
    acw: 'After call work'
  };
  agentUuid: string;
  duration: string;
  extensionKey: string;
  extensionLabel: string;
  remark: string;
  state: string;

  constructor(obj?: Partial<StateReport>) {
    if (obj) {
      Object.assign(this, obj);
    }
  }

  get displayState() {
    return this.stateMapping[this.state];
  }
}

export interface CompletedCallReport {
  'ACW Duration': string;
  'Answer Time': string;
  Caller: string;
  Queue: string;
  Result: string;
  'Start At': string;
  'Talk Duration': string;
  'Txn UUID': string;
  User: string;
  'Failed Reason': string;
  'Callback Number': string;
  'Callback Start At': string;
  'Incoming Caller ID': string;
  'Incoming Txn UUID': string;
  'User Answer Time': string;
  queueUuid: string;
}

export interface AssignedCallReport {
  'Talk Duration': string;
  Type: 'Autodialer' | 'Incoming' | 'Callback';
  User: string;
  'Start At': string;
  From: string;
  To: string;
  'Incoming Txn UUID': string;
  Queue: string;
  'Answer Time': string;
  'Txn UUID': string;
  Result: string;
  queueUuid: string;
  agentUuid: string;
}

export interface AssignedCallReqFilter {
  'callcenter.queueType': 'incoming' | 'callback';
  accessors: string;
  'callcenter.queueUuid': string[];
  'callcenter.result': string[];
}

export class QueuePerformanceReport {
  'Queue Overflow Calls': string;
  'Max Wait Duration': string;
  'incoming.count.overflow': string;
  'Average Abandoned Duration (AAD)': string;
  Voicemail: string;
  'incoming.longAbandoned': string;
  'incoming.sum.queueAbandonedDuration': string;
  'Calls Reached Queue': string;
  'Short Abandoned Calls': string;
  'Callback Request': string;
  'incoming.answeredWithin': string;
  'SLA (%)': string;
  'incoming.abandonedWithin': string;
  'Average Handle Duration': string;
  'incoming.sum.talkDuration': string;
  'Average Speed of Answer (ASA)': string;
  'incoming.count.answered': string;
  'Unreachable Calls': string;
  'SLA Threshold (second)': string;
  'Queue UUID': string;
  'Long Abandoned Calls': string;
  'incoming.sum.wrapDuration': string;
  'incoming.count.unreachable': string;
  'incoming.count.all': string;
  'Queue Name': string;
  'incoming.sum.queueDuration': string;
  'incoming.sum.queueAnsweredDuration': string;
  'Average Wait Duration': string;
  'incoming.count.abandoned': string;
  'Answered Calls within SLA': string;
  'Average Talk Duration': string;
  'Answered Calls': string;
  'Assigned Calls': string;
  'Max Talk Duration': string;

  constructor(obj?: Partial<QueuePerformanceReport>) {
    if (obj) {
      Object.assign(this, obj);
    }
  }

  get displayName() {
    return this['Queue Name']?.length > 16 ? this['Queue Name']?.slice(0, 16) + '...' : this['Queue Name'];
  }

  get tooltip() {
    return this['Queue Name']?.length > 16 ? this['Queue Name'] : '';
  }
}

export interface QueuePerformanceFilterReq {
  queueUuid: string[];
}

export interface UserDataLoginHistory {
  'Date Time': number;
  'Device Type': string;
  Duration: string;
  'IP Address': string;
  Successful: string;
  'User Agent': string;
  'Failed Reason': string;
  Country: string;
}

export interface DatasetEsIndex {
  dataset: string;
  description: string;
  esIndex: string;
}

export interface DatasetEsIndexFields {
  esIndex: string;
  fields: FieldEsIndex[];
  scoped: boolean;
}

export interface FieldEsIndex {
  indexed: boolean;
  key: string;
  type: string;
}

export class SecurityEventRes {
  columnOrder: string[];
  rows: SecurityEventsData[];

  constructor(obj?: Partial<SecurityEventRes>) {
    if (obj) {
      Object.assign(this, obj);
    }
  }
}

export class SecurityEventsData {
  actor: Actor;
  details: DetailsSE;
  eventName: string;
  ipAddress: string;
  time: string;
}

export interface Actor {
  identityUuid: string;
  name: string;
  type: string;
}

export interface DetailsSE {
  device: string;
  sipAccount: string;
  sipDomain: string;
}

export interface SecurityEventReq {
  startTime: string;
  endTime: string;
  orgUuid?: string;
  searchTags?: any;
  filter?: HashMap<string | string[]>;
}
